import {useCallback, useRef} from 'react';
import {CodeSandboxPreview, CompiledInfo, SetupBridge} from '@baidu/code-live-render';
import {useMemo} from 'react';
import dayjs from 'dayjs';
import {LogIdMap} from '../../constant';
import {utils as KuUtils} from '@/kuTools/sdk/utils';
import {flexBuilderInstanceObj} from '@/kuTools/utils';
import styles from './index.module.scss';
import sdk from '@/sdk';
import {iframeEnvMap} from '../../configs';
import * as store from '@/store';
import {injectMorphoAPI} from '../MorphoAPIInjector';

export type {CompiledInfo};

const env = process.env.REACT_APP_ENVIRONMENT;
const IframeUrl = env ? iframeEnvMap[env] : iframeEnvMap.online;

interface IProps {
    code: string;
    builderId: string;
    compiledInfo?: CompiledInfo;
    onCompiled?: (compiledInfo: CompiledInfo | null) => void;
    onReady?: () => void;
    onError?: (error: Error) => void;
    mode?: 'preview' | 'edit'; // 知识库文档内组件是 preview 模式，全屏编辑时是 edit 模式
    readonly?: boolean;
    isArtifactLoading?: boolean;
    hScreen?: boolean; // 随心搭内强制使用height: 100vh；用于演示模式下随心搭满屏
    onSizeChange?: (params: [width: number, height: number]) => void;
    onRetryBtnClick?: () => void;
    showRetryBtn?: boolean;
}

export const CodePreviewComponent = (props: IProps) => {
    const {
        code,
        builderId,
        compiledInfo = {compiledCode: '', dependencies: [], style: ''},
        onCompiled,
        onReady,
        onError,
        mode = 'preview',
        readonly = false,
        isArtifactLoading = false,
        hScreen,
        onSizeChange,
        onRetryBtnClick,
        showRetryBtn = false,
    } = props;

    // artifact 生成过程中，不更新 code，避免重复 compile
    const cachedCodeRef = useRef(code);
    if (!isArtifactLoading) {
        cachedCodeRef.current = code;
    }

    const handleError = useCallback((error: Error) => {
        onError && onError(error);
    }, [onError]);

    const handleCompiled = (data: CompiledInfo | null) => {
        onCompiled && onCompiled(data);
    };
    const handleReady = () => {
        onReady && onReady();
    };

    const handleClick = () => {
        const flexBuilderData = flexBuilderInstanceObj.get(builderId);
        KuUtils.kuLogger({
            repId: LogIdMap.InteractComponent,
            params: {
                builderId,
                title: flexBuilderData?.title,
                createTime: dayjs(flexBuilderData?.createTime).format('YYYY-MM-DD HH:mm:ss'),
            },
        });
    };

    const handleSizeChange = (params: [width: number, height: number]) => {
        onSizeChange?.(params);
    };

    const renderProps = useMemo(() => ({
        isMobile: false,
        readOnly: readonly,
    }), [readonly]);

    const previewProps =
        mode === 'edit' || !compiledInfo.compiledCode
            ? {code: cachedCodeRef.current}
            : {compiledComponent: compiledInfo};

    const setup = useCallback((bridge: SetupBridge) => {

        // 获取ugateToken
        bridge.injectApi('Ku.sdk.getUgateToken', async (targetAppKey) => {
            const token = await KuUtils.getToken(targetAppKey);
            return token;
        });

        // 获取用户的token
        bridge.injectApi('Ku.sdk.getUserToken', async () => {
            return sdk.as?.user?.token;
        });

        // 获取文档的rte token
        bridge.injectApi('Ku.sdk.getRteToken', async () => {
            return store.getData(store.KEYS.CUR_DOCUMENT_TOKEN).accessToken;
        });

        // 注册 morpho api，可获取和操作文档数据
        injectMorphoAPI(bridge, builderId);

    }, [builderId]);

    // 组件报错，重新生成
    const handleRetryBtnClick = useCallback(() => {
        onRetryBtnClick?.();
    }, [onRetryBtnClick]);

    return (
        <CodeSandboxPreview
            id={builderId}
            className={styles.container}
            iframeUrl={IframeUrl}
            onError={handleError}
            onCompiled={handleCompiled}
            onReady={handleReady}
            {...previewProps}
            data-zoom-disable="true"
            hScreen={hScreen}
            renderProps={renderProps}
            onClick={handleClick}
            onSizeChange={handleSizeChange}
            setup={setup}
            onErrorRetry={handleRetryBtnClick}
            showErrorRetry={showRetryBtn}
        />
    );
};
